import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { logIn } from '../../services/auth';
import { parlorScreen, useDesign } from '../../designs';
import DesignButton from '../../components/ui/DesignButton';
import Input from '../../components/ui/Input';

const d = parlorScreen.tokens;

export default function LoginScreen() {
  const navigate = useNavigate();
  const { typography } = useDesign();
  const displayFont = `'${typography.displayFont}', serif`;
  const fs = typography.fontSize;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await logIn(email, password);
      navigate('/');
    } catch (err: unknown) {
      const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : '';
      if (code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError('Incorrect password');
      } else if (code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Unable to sign in. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '32px',
      backgroundColor: d.bgPrimary,
    }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: displayFont, fontSize: '28px', fontWeight: 700,
            color: d.textPrimary, margin: 0,
          }}>
            KREWE
          </h1>
        </div>

        {/* Screen title */}
        <h2 style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '3px', textTransform: 'uppercase',
          color: d.textSecondary, textAlign: 'center', marginBottom: '24px',
        }}>
          Sign In
        </h2>

        {/* Divider */}
        <div style={{
          height: '1px', marginBottom: '24px',
          background: `linear-gradient(to right, transparent, rgba(200,146,58,0.25), transparent)`,
        }} />

        <form onSubmit={handleSubmit}>
          <Input label="Email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          <Input label="Password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />

          {error && (
            <p style={{
              color: d.negative, fontFamily: "'Raleway', sans-serif",
              fontSize: fs.detail, marginBottom: '16px', textAlign: 'center',
            }}>
              {error}
            </p>
          )}

          <DesignButton tier={2} fullWidth type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </DesignButton>
        </form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Link to="/forgot-password" style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
            color: d.textSecondary, textDecoration: 'none',
          }}>
            Forgot password?
          </Link>
        </div>

        <div style={{
          textAlign: 'center', marginTop: '24px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary,
        }}>
          Don't have an account?{' '}
          <Link to="/signup" style={{ color: d.accent, textDecoration: 'none', fontWeight: 500 }}>
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}
